import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslation } from '@mukil/i18n';
import { cn } from '../../lib/cn';

const alertVariants = cva(
  'flex items-start gap-3 rounded-xl px-4 py-3 text-sm border',
  {
    variants: {
      variant: {
        info: 'bg-[rgba(31,58,95,0.06)] border-[rgba(31,58,95,0.18)] text-[color:var(--accent)]',
        success:
          'bg-[rgba(21,128,61,0.06)] border-[rgba(21,128,61,0.20)] text-[color:var(--color-success)]',
        warning:
          'bg-[rgba(245,158,11,0.06)] border-[rgba(245,158,11,0.25)] text-[color:var(--color-warning-text)]',
        danger:
          'bg-[rgba(220,38,38,0.06)] border-[rgba(220,38,38,0.20)] text-[color:var(--color-danger)]',
        default:
          'bg-[rgba(31,58,95,0.04)] border-[rgba(31,58,95,0.12)] text-ink',
        destructive:
          'bg-[rgba(220,38,38,0.06)] border-[rgba(220,38,38,0.20)] text-[color:var(--color-danger)]',
      },
    },
    defaultVariants: { variant: 'info' },
  },
);

type AlertVariantProps = VariantProps<typeof alertVariants>;

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>, AlertVariantProps {
  title?: string;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ variant, title, children, className, ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      <div className="flex-1">
        {title && <p className="font-semibold mb-0.5">{title}</p>}
        <div className="text-[0.8125rem] opacity-90">{children}</div>
      </div>
    </div>
  ),
);
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 font-medium leading-none tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

const AlertClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function AlertCloseInner({ className, ...props }, ref) {
  const { t } = useTranslation('common');
  return (
    <button
      ref={ref}
      className={cn(
        'absolute end-4 top-4 rounded-none p-1 opacity-70 hover:opacity-100',
        className,
      )}
      {...props}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
      <span className="sr-only">{t('close')}</span>
    </button>
  );
});
AlertClose.displayName = 'AlertClose';

export { Alert, AlertTitle, AlertDescription, AlertClose, alertVariants };
export type { AlertProps };
